import React, { useState, useEffect } from "react";
import {
  X,
  Search,
  Building2,
  BadgeDollarSign,
  Users,
  ShoppingCart,
  Bell,
  Puzzle,
} from "lucide-react";

import { useNavigate } from "react-router-dom";

const SettingsDrawer = ({ open, onClose, cards = [], onMenuClick }) => {
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  // Close drawer on ESC key press
  useEffect(() => {
    if (!open) return;

    const handleEsc = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [open, onClose]);

  
// Add required menu items if missing
const enrichedCards = cards.map((card) => {
  // =====================================================
  // ORGANISATION
  // =====================================================
  if (
    card.title === "Organisation" ||
    card.title === "Organization"
  ) {
    const currentItems = card.items.map((item) =>
      item.toLowerCase()
    );

    const additions = [];

    if (!currentItems.includes("bank")) {
      additions.push("Bank");
    }

    if (!currentItems.includes("numbering configuration")) {
      additions.push("Numbering Configuration");
    }

    if (!currentItems.includes("document create")) {
      additions.push("Document Create");
    }

    return {
      ...card,
      items: [...card.items, ...additions],
    };
  }


  // =====================================================
  // INTEGRATIONS & MARKETPLACE
  // =====================================================
  if (
    card.title === "Integrations & Marketplace"
  ) {
    const currentItems = card.items.map((item) =>
      item.toLowerCase()
    );

    const additions = [];

    if (!currentItems.includes("whatsapp cloud api")) {
      additions.push("WhatsApp Cloud API");
    }

    if (!currentItems.includes("whatsapp templates")) {
      additions.push("WhatsApp Templates");
    }

    return {
      ...card,
      items: [...card.items, ...additions],
    };
  }


  return card;
});

  // Filter cards and items based on search term
  const filteredCards = enrichedCards
    .map((card) => ({
      ...card,
      items: card.items.filter((item) =>
        item.toLowerCase().includes(search.toLowerCase())
      ),
    }))
    .filter(
      (card) =>
        card.title.toLowerCase().includes(search.toLowerCase()) ||
        card.items.length > 0
    );

  if (!open) return null;

  const iconMap = {
    Organisation: <Building2 className="w-7 h-7 text-blue-600" />,
    Organization: <Building2 className="w-7 h-7 text-blue-600" />,
    "Tax & Compliance": <BadgeDollarSign className="w-7 h-7 text-teal-600" />,
    Users: <Users className="w-7 h-7" />,
    Sales: <ShoppingCart className="w-7 h-7 text-pink-600" />,
    "Reminders & Notifications": <Bell className="w-7 h-7 text-yellow-500" />,
    "Integrations & Marketplace": <Puzzle className="w-7 h-7 text-purple-600" />,
  };

  // Handle item click inside card
  const handleItemClick = (card, item, e) => {
    e.stopPropagation(); // prevent card onClick
    if (onMenuClick) onMenuClick(card.title, item);
    onClose();

    // Optionally you may remove internal navigation here if handled by parent
    if (
      (card.title === "Organisation" || card.title === "Organization") &&
      item.toLowerCase() === "numbering configuration"
    ) {
      navigate("/settings/numbering");
    } else if (
      (card.title === "Organisation" || card.title === "Organization") &&
      item.toLowerCase() === "document create"
    ) {
      navigate("/documents/create");
    } else if (
      item.toLowerCase() === "bank" &&
      (card.title === "Organisation" || card.title === "Organization")
    ) {
      navigate("/settings/bank");
    }
  };

  return (
    <div
      style={{
        position: "absolute",
        top: 56,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        zIndex: 1200,
      }}
      aria-modal="true"
      role="dialog"
    >
      <div
        className="relative bg-white rounded-none flex flex-col overflow-hidden animate-fadeIn"
        tabIndex={-1}
      >
        <div
          className="flex items-center justify-between px-4 py-2 border-b bg-gradient-to-r to-blue-100 min-h-[48px]"
          aria-label="Settings drawer header"
        >
          <h2
            id="settings-drawer-title"
            className="text-lg font-normal tracking-tight text-gray-700 m-0 flex-shrink-0"
          >
            All Settings
          </h2>
          <div className="flex-grow max-w-xl mx-4">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search settings"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search settings"
                className="w-full pl-10 pr-4 py-1.5 rounded-md border border-gray-200 bg-white text-[16px] text-gray-700 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition shadow-sm"
              />
            </div>
          </div>
          <button
            className="p-2 rounded-full hover:bg-gray-200 transition"
            onClick={onClose}
            aria-label="Close"
            type="button"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <div className="flex-grow overflow-auto bg-gradient-to-br from-blue-50 via-white to-indigo-50 px-8 pt-8 pb-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredCards.length === 0 ? (
              <div className="col-span-full text-center text-gray-400 py-24 text-lg">
                No settings found.
              </div>
            ) : (
              filteredCards.map((card, idx) => (
                <div
                  key={idx}
                  className="bg-white rounded-xl shadow-md border border-gray-100 p-6 cursor-pointer flex flex-col min-h-[185px] hover:border-blue-400 hover:shadow-lg transition group"
                  onClick={card.onClick}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      card.onClick();
                      e.preventDefault();
                    }
                  }}
                  aria-label={`Open settings for ${card.title}`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    {iconMap[card.title] || (
                      <Building2 className="w-7 h-7 text-gray-300" />
                    )}
                    <span className="text-lg font-semibold text-gray-700">
                      {card.title}
                    </span>
                  </div>
                  <ul className="ml-2 mt-2 text-gray-600 text-[15px] space-y-1">
                    {card.items.map((item, i) => (
                      <li
                        key={i}
                        className="list-disc cursor-pointer hover:text-blue-600"
                        onClick={(e) => handleItemClick(card, item, e)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleItemClick(card, item, e);
                          }
                        }}
                        tabIndex={0}
                        role="button"
                        aria-label={`Open setting for ${item}`}
                      >
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsDrawer;
